import { useMemo, useCallback } from 'react';
import axios from 'axios';
import { auth } from '../firebase'; // Adjust path as necessary if hook is moved

// Prefer env override; fallback to relative URLs so CRA dev proxy can handle local backend.
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.servicegest.ro'

// const API_BASE_URL = 'http://localhost:8080'; // For local development with CRA proxy
const useApiClient = () => {
  const _request = useCallback(async (method, path, data = null, options = {}) => {
    const { baseAPI, overrideToken, returnFullResponse, ...axiosOptions } = options;

    let token;
    if (overrideToken) {
      token = overrideToken;
    } else {
      if (!auth.currentUser) {
        console.error("API Client Error: User not authenticated.");
        throw new Error("User not authenticated."); // Or a custom error object
      }

      try {
        token = await auth.currentUser.getIdToken();
      } catch (tokenError) {
        console.error("API Client Error: Failed to get ID token.", tokenError);
        throw new Error("Failed to get ID token."); // Or a custom error object
      }
    }

    const headers = {
      'Authorization': `Bearer ${token}`,
    };

    // Only set JSON Content-Type when payload is not FormData
    if (
      data &&
      !(typeof FormData !== 'undefined' && data instanceof FormData) &&
      (method.toLowerCase() === 'post' || method.toLowerCase() === 'put' || method.toLowerCase() === 'patch' || method.toLowerCase() === 'delete')
    ) {
      headers['Content-Type'] = 'application/json';
    }

    const url = `${baseAPI || API_BASE_URL}${path}`;

    try {
      const response = await axios({
        method,
        url,
        headers,
        data,
        ...axiosOptions, // Spread additional axios options like responseType
      });
      return returnFullResponse ? response : response.data; // Return full response if requested, otherwise just data
    } catch (error) {
      // Log the error and re-throw to be handled by the calling component
      console.error(`API Client ${method.toUpperCase()} Error on ${url}:`, error.response ? error.response.data : error.message, error);
      throw error;
    }
  }, []);

  // Helper function to build pagination query parameters
  const buildPaginationParams = useCallback((paginationOptions = {}) => {
    const { page, limit, offset, ...otherParams } = paginationOptions;
    const params = new URLSearchParams();
    
    if (page !== undefined) params.append('page', page.toString());
    if (limit !== undefined) params.append('limit', limit.toString());
    if (offset !== undefined) params.append('offset', offset.toString());
    
    // Add any other custom parameters
    Object.entries(otherParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    return params.toString();
  }, []);

  // Enhanced request method for paginated endpoints
  const _paginatedRequest = useCallback(async (method, path, data = null, paginationOptions = {}, options = {}) => {
    const queryString = buildPaginationParams(paginationOptions);
    const separator = path.includes('?') ? '&' : '?';
    const paginatedPath = queryString ? `${path}${separator}${queryString}` : path;
    
    const response = await _request(method, paginatedPath, data, { ...options, returnFullResponse: true });
    
    // Extract pagination metadata from response headers or data
    const paginationMeta = {
      page: paginationOptions.page,
      limit: paginationOptions.limit,
      offset: paginationOptions.offset,
      // Add common pagination metadata that backends typically return
      totalCount: response.headers['x-total-count'] ? parseInt(response.headers['x-total-count']) : undefined,
      totalPages: response.headers['x-total-pages'] ? parseInt(response.headers['x-total-pages']) : undefined,
      hasNextPage: response.headers['x-has-next-page'] === 'true',
      hasPrevPage: response.headers['x-has-prev-page'] === 'true',
    };
    
    return {
      data: response.data,
      pagination: paginationMeta,
      headers: response.headers,
      status: response.status,
    };
  }, [_request, buildPaginationParams]);

  // Standard methods (unchanged for backward compatibility)
  const get = useCallback((path, options) => _request('get', path, null, options), [_request]);
  const post = useCallback((path, payload, options) => _request('post', path, payload, options), [_request]);
  const put = useCallback((path, payload, options) => _request('put', path, payload, options), [_request]);
  const patch = useCallback((path, payload, options) => _request('patch', path, payload, options), [_request]);
  const del = useCallback((path, payload = null, options) => _request('delete', path, payload, options), [_request]);

  // Paginated methods
  const getPaginated = useCallback((path, paginationOptions, options) => 
    _paginatedRequest('get', path, null, paginationOptions, options), [_paginatedRequest]);
  const postPaginated = useCallback((path, payload, paginationOptions, options) => 
    _paginatedRequest('post', path, payload, paginationOptions, options), [_paginatedRequest]);
  const putPaginated = useCallback((path, payload, paginationOptions, options) => 
    _paginatedRequest('put', path, payload, paginationOptions, options), [_paginatedRequest]);
  const deletePaginated = useCallback((path, paginationOptions, payload = null, options) => 
    _paginatedRequest('delete', path, payload, paginationOptions, options), [_paginatedRequest]);

  return useMemo(() => ({
    // Standard methods
    get,
    post,
    put,
    patch,
    delete: del,
    // Paginated methods
    getPaginated,
    postPaginated, 
    putPaginated,
    deletePaginated,
    // Utility function for custom pagination needs
    buildPaginationParams,
  }), [get, post, put, del, getPaginated, postPaginated, putPaginated, deletePaginated, buildPaginationParams]);
};

export default useApiClient; 